/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import "../assets/css/bootstrap/bootstrap.css";
import "../assets/css/bootstrap/bootstrap-grid.css";
import "../assets/css/bootstrap/bootstrap-reboot.css";
import "../assets/css/backend-plugin.min.css";
import "../assets/vendor/remixicon/fonts/remixicon.css";
import "../assets/vendor/line-awesome/dist/line-awesome/css/line-awesome.min.css";
import "../assets/vendor/@fortawesome/fontawesome-free/css/all.min.css";
import { useTranslate } from "../hooks/LanguageProvider";
import AuthLayout from "../components/auth/AuthLayout";
import PasswordField from "../components/auth/PasswordField";
import PolicyCheckbox from "../components/auth/PolicyCheckbox";
import AuthAlert from "../components/auth/AuthAlert";
import { useSignupForm } from "../hooks/useSignupForm";
import {
  IoBriefcaseOutline,
  IoCallOutline,
  IoMailOutline,
  IoPersonOutline,
} from "react-icons/io5";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

type RoleValue = "supplier" | "customer" | "expert";

const fieldIconStyle: React.CSSProperties = {
  color: "#6b7280",
  fontSize: "18px",
};

const normalizePhoneInput = (value: string) => {
  const cleaned = value.replace(/[^\d+]/g, "");

  if (/^0[567]\d{8}$/.test(cleaned)) {
    return `+213${cleaned.slice(1)}`;
  }

  if (/^[567]\d{8}$/.test(cleaned)) {
    return `+213${cleaned}`;
  }

  if (/^213[567]\d{8}$/.test(cleaned)) {
    return `+${cleaned}`;
  }

  if (cleaned.startsWith("00213")) {
    return `+${cleaned.slice(2)}`;
  }

  return cleaned;
};

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useTranslate();
  const form = useSignupForm({
    backendUrl,
    translate,
    onSuccess: () => navigate("/login"),
  });

  const primaryButtonLabel = form.isVerificationStep
    ? translate("Verify code and create account")
    : "Create Account";

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && form.signupButtonRef.current) {
        form.signupButtonRef.current.click();
      }
    };
    document.body.addEventListener("keypress", handleKeyPress);
    return () => document.body.removeEventListener("keypress", handleKeyPress);
  }, [form]);

  return (
    <AuthLayout>
      <div style={{ width: "100%", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px", textAlign: "center" }}>
          <h2
            style={{
              margin: 0,
              color: "#111827",
              fontSize: "30px",
              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            {translate("Sign Up")}
          </h2>
          <p
            style={{
              margin: "8px 0 0",
              color: "#6b7280",
              fontSize: "15px",
              lineHeight: 1.5,
            }}
          >
            {translate("Member space")}
          </p>
        </div>

      <form className="smart-form client-form client-form-size" autoComplete="off" style={{ width: "100%" }}>
        <div id="resText"></div>

        <div className="row" style={{ rowGap: "16px" }}>
          <div className="col-lg-6">
            <label className="form-label fw-semibold" htmlFor="signup-username">{translate("Username")}</label>
            <div className="input-group">
              <span className="input-group-text bg-white">
                <IoPersonOutline style={fieldIconStyle} />
              </span>
              <input
                className="form-control"
                type="text"
                placeholder={translate("Enter your name")}
                name="signup-username"
                id="signup-username"
                autoComplete="off"
                value={form.username}
                disabled={form.isFormLocked}
                onChange={(e) => form.setUsername(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-6">
            <label className="form-label fw-semibold" htmlFor="signup-email">{translate("Email")}</label>
            <div className="input-group">
              <span className="input-group-text bg-white">
                <IoMailOutline style={fieldIconStyle} />
              </span>
              <input
                className="form-control"
                type="email"
                placeholder={translate("Enter your email")}
                name="signup-email"
                id="signup-email"
                autoComplete="off"
                value={form.email}
                disabled={form.isFormLocked}
                onChange={(e) => form.setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-6">
            <label className="form-label fw-semibold" htmlFor="signup-phone">{translate("Phone")}</label>
            <div className="input-group">
              <span className="input-group-text bg-white">
                <IoCallOutline style={fieldIconStyle} />
              </span>
              <input
                className="form-control"
                type="tel"
                placeholder="0555123456"
                name="signup-phone"
                id="signup-phone"
                autoComplete="off"
                value={form.phone}
                disabled={form.isFormLocked}
                onChange={(e) => form.setPhone(normalizePhoneInput(e.target.value))}
              />
            </div>
          </div>

         <div className="col-lg-6">
            <label className="form-label fw-semibold" htmlFor="signup-role">{translate("Role")}</label>
            <div className="input-group">
              <span className="input-group-text bg-white">
                <IoBriefcaseOutline style={fieldIconStyle} />
              </span>
              <select
                className="form-control"
                name="signup-role"
                id="signup-role"
                value={form.role}
                disabled={form.isFormLocked}
                onChange={(e) => form.setRole(e.target.value as RoleValue)}
              >
                <option value="supplier">{translate("Supplier")}</option>
                <option value="customer">{translate("OEM")}</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>

          <PasswordField
            label={translate("Password")}
            id="new-password"
            value={form.password}
            disabled={form.isFormLocked}
            onChange={form.setPassword}
          />

          <PasswordField
            label={translate("Confirm Password")}
            id="confirm-password"
            value={form.confirmPassword}
            disabled={form.isFormLocked}
            onChange={form.setConfirmPassword}
          />

          

          {form.isVerificationStep && (
            <div className="col-lg-12">
              <label>{translate("Verification Code")}</label>
              <div className="floating-label form-group">
                <input
                  className="floating-input form-control"
                  type="text"
                  placeholder={translate("Enter the code sent to your phone")}
                  name="verificationCode"
                  id="verificationCode"
                  value={form.verificationCode}
                  disabled={form.loading}
                  onChange={(e) => form.setVerificationCode(e.target.value)}
                />
              </div>

              {form.verificationMessage && (
                <p className="text-muted mb-1">{form.verificationMessage}</p>
              )}

              <p className="text-muted mb-3">
                {translate("Attempts remaining")}: {form.attemptsLeft}
              </p>
            </div>
          )}

          <div className="text-center mb-1 mt-2 col-lg-12">
            <button
              type="button"
              className="btn col-lg-12 mx-auto d-block"
              style={{
                background: "#ffc928",
                borderColor: "#ffc928",
                color: "#111827",
                borderRadius: "6px",
                fontWeight: 700,
                minHeight: "46px",
                boxShadow: "0 8px 18px rgba(255, 201, 40, 0.24)",
              }}
              onClick={form.submit}
              ref={form.signupButtonRef}
              disabled={form.loading}
            >
              {primaryButtonLabel}
            </button>
          </div>

          {form.isVerificationStep && (
            <>
              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-outline-primary col-lg-12 mx-auto d-block"
                  onClick={form.resendVerificationCode}
                  disabled={form.loading}
                >
                  {translate("Resend code")}
                </button>
              </div>

              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-light col-lg-12 mx-auto d-block"
                  onClick={form.cancelVerification}
                  disabled={form.loading}
                >
                  {translate("Change phone number")}
                </button>
              </div>
            </>
          )}

          <PolicyCheckbox
            checked={form.agreePolicy}
            onChange={form.setAgreePolicy}
            translate={translate}
            disabled={form.isFormLocked}
          />

          {form.loading && (
            <div className="text-center">
              <BeatLoader color={"#123abc"} loading={form.loading} size={12} />
            </div>
          )}
        </div>

        <p className="text-center mt-2">
          <span>{translate("Already have an account?")}</span>
          <Link to="/login">
            <span>&nbsp;{translate("Sign in instead")}</span>
          </Link>
        </p>
        <AuthAlert message={form.alertMessage} />
      </form>
      </div>
    </AuthLayout>
  );
};

export default SignUp;
